import type { CardProps } from "@heroui/react";

import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react";
import React from "react";
import { Card, CardContent, CardHeader } from "@heroui/react";

export type FeatureCardProps = Partial<CardProps> & {
  key: string;
  title: string;
  icon: string;
  descriptions: string[];
};

export type FeatureCardPropsWithSelect = FeatureCardProps & {
  onSelectFn: (description: string) => void;
};

export const FeatureCard = React.forwardRef<HTMLDivElement, FeatureCardPropsWithSelect>(
  ({ title, icon, descriptions, onSelectFn, ...props }, ref) => {
    const { t } = useTranslation();

    return (
      <Card ref={ref} className="bg-content2 shadow-none" {...props}>
        <CardHeader className="flex flex-col items-center gap-2 px-4 pb-4 pt-6">
          <Icon className="text-4xl" icon={icon} width={48} />
          <p className="text-medium text-content2-foreground">{t(title)}</p>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          {descriptions.map((description, index) => (
            <div
              key={index}
              className="flex min-h-[50px] cursor-pointer rounded-medium bg-content3 px-3 py-2 text-content3-foreground hover:bg-content4"
              role="button"
              tabIndex={0}
              onClick={() => onSelectFn(description)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelectFn(description);
                }
              }}
            >
              <p className="text-small">{t(description)}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  },
);

FeatureCard.displayName = "FeatureCard";

export const FeatureCards = ({
  cards,
  onSelectFn,
}: {
  cards: FeatureCardProps[];
  onSelectFn: (prompt: string) => void;
}) => {
  return (
    <div className="flex h-full flex-col justify-center gap-4 p-4">
      <>
        <div className="grid grid-cols-1 gap-4 pt-10 min-[480px]:grid-cols-2 lg:grid-cols-3">
          {Array.isArray(cards) &&
            cards.map((card) => (
              <FeatureCard
                key={card.key}
                descriptions={card.descriptions}
                icon={card.icon}
                title={card.title}
                onSelectFn={onSelectFn}
              />
            ))}
        </div>
      </>
    </div>
  );
};
